import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import { ResponsiveImage } from '../../components';
import { MAX_SLIDER_IMAGES } from '../../util/search';

import css from './ListingCard.module.css';

// Chevron for the desktop hover arrows. `fill: none` lives in CSS (.navIcon) to
// override the app-wide `svg { fill: currentColor }` rule, which would otherwise
// render this stroke icon as a filled blob.
const Chevron = ({ direction }) => (
  <svg
    className={css.navIcon}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={direction === 'prev' ? 'M15 18 9 12l6-6' : 'm9 18 6-6-6-6'} />
  </svg>
);

const imageVariants = (image, variantPrefix) =>
  image
    ? Object.keys(image.attributes?.variants || {}).filter(k => k.startsWith(variantPrefix))
    : [];

/**
 * Horizontal photo slider for a listing card — the search-result equivalent of the
 * gallery on the listing page, so a visitor can size up a space without leaving the
 * results.
 *
 * Built on native scroll-snap rather than a JS carousel: swiping, momentum and
 * accessibility come from the platform, and there is no library to add
 * (see CLAUDE.md — no new npm packages).
 *
 * Two deliberate constraints:
 *  - `scroll-snap-type` is applied only after the first pointer/touch on the card,
 *    never while the page loads. A snap container present during load is what made
 *    Lighthouse report NO_LCP on the homepage (PR #51); gating it on interaction
 *    means no snap container exists until well after the LCP is painted.
 *  - Only the first photo is in the DOM until that same first interaction, so the
 *    SSR markup, the first client render and today's image payload are unchanged.
 *    Hydration therefore matches exactly (see CLAUDE.md — SSR & Hydration).
 *
 * @component
 * @param {Object} props
 * @param {Array} props.images listing image entities (first one is the cover)
 * @param {string} props.title listing title, used as image alt text
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {string} props.renderSizes img/srcset size rules
 * @param {boolean} props.highImagePriority eager-load the cover (above-the-fold card)
 * @returns {JSX.Element}
 */
const ListingCardImageSlider = props => {
  const { images, title, variantPrefix, renderSizes, highImagePriority } = props;

  const slides = (images || []).slice(0, MAX_SLIDER_IMAGES);
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // One flag for both progressive rendering and scroll-snap — see the note above.
  const [interacted, setInteracted] = useState(false);

  const lastIndex = slides.length - 1;
  // Before the first interaction only the cover exists, exactly as before this
  // component; afterwards we keep one slide ahead of the visitor ready.
  const renderUpTo = interacted ? Math.min(activeIndex + 1, lastIndex) : 0;

  const onInteract = useCallback(() => setInteracted(true), []);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !el.clientWidth) {
      return;
    }
    const index = Math.round(el.scrollLeft / el.clientWidth);
    setActiveIndex(prev => (prev === index ? prev : index));
  }, []);

  const goTo = useCallback((index, event) => {
    // The card root is a link: a click on the arrows must not open the listing.
    event.preventDefault();
    event.stopPropagation();
    const el = scrollerRef.current;
    if (!el) {
      return;
    }
    setInteracted(true);
    el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  }, []);

  return (
    <div className={css.slider} onPointerEnter={onInteract} onTouchStart={onInteract}>
      <div
        className={classNames(css.sliderTrack, { [css.sliderTrackSnap]: interacted })}
        ref={scrollerRef}
        onScroll={handleScroll}
      >
        {slides.map((image, index) => {
          const isCover = index === 0;
          return (
            <div className={css.slide} key={image.id?.uuid || index}>
              {index <= renderUpTo ? (
                <ResponsiveImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={image}
                  variants={imageVariants(image, variantPrefix)}
                  sizes={renderSizes}
                  fetchpriority={isCover && highImagePriority ? 'high' : 'low'}
                  // Slides past the cover only mount once the visitor has reached
                  // for them, so loading them eagerly is what they just asked for.
                  loading={isCover && !highImagePriority ? 'lazy' : 'eager'}
                />
              ) : null}
            </div>
          );
        })}
      </div>

      {slides.length > 1 ? (
        <>
          {/* Mouse-only affordances. They sit inside the card's <a>, so they are
              plain spans rather than <button>s — interactive elements may not nest
              inside a link. Both are removed from the accessibility tree: keyboard
              and screen-reader users keep the card's single link as their one tab
              stop and open the listing to see the full gallery, exactly as before. */}
          <span
            className={classNames(css.navButton, css.navPrev, {
              [css.navHidden]: activeIndex <= 0,
            })}
            onClick={e => goTo(activeIndex - 1, e)}
            role="presentation"
            aria-hidden="true"
          >
            <Chevron direction="prev" />
          </span>
          <span
            className={classNames(css.navButton, css.navNext, {
              [css.navHidden]: activeIndex >= lastIndex,
            })}
            onClick={e => goTo(activeIndex + 1, e)}
            role="presentation"
            aria-hidden="true"
          >
            <Chevron direction="next" />
          </span>
          <span className={css.dots} aria-hidden="true">
            {slides.map((image, index) => (
              <span
                className={classNames(css.dot, { [css.dotActive]: index === activeIndex })}
                key={image.id?.uuid || index}
              />
            ))}
          </span>
        </>
      ) : null}
    </div>
  );
};

export default ListingCardImageSlider;
